import type { PayloadAction } from '@reduxjs/toolkit';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { INITIAL_PAGINATION_INFO } from '@routes/profile-bar/___pages___/my-results/___shared___/constants.ts';
import type {
    ImportedLearningsFiltersValues,
    ImportedLearningsRequest,
    UserImportedLearning,
} from '@routes/profile-bar/___pages___/my-results/api/importedLearnings.api.ts';
import { getMyImportedLearningResults } from '@routes/profile-bar/___pages___/my-results/api/importedLearnings.api.ts';
import { IMPORTED_LEARNINGS_REQUEST_KEY } from '@routes/profile-bar/___pages___/my-results/ui/tables/my-imported-learnings/constants.ts';
import type { PaginatedResponse } from '@types';
import { extractError } from '@utils/slice.ts';

export interface ImportedLearningsError {
    message?: string;
    [k: string]: any;
}

export interface ImportedLearningsState {
    error: ImportedLearningsError | null;

    myImportedLearningResults: PaginatedResponse<UserImportedLearning, ImportedLearningsFiltersValues>;
    myImportedLearningsRequestData: ImportedLearningsRequest;
    currentGetRequestId: string | null;

    isGetImportedLearningResultsPending: boolean;
    isGetImportedLearningsFiltersValuesPending: boolean;
}

const initialState: ImportedLearningsState = {
    error: null,

    myImportedLearningResults: {
        data: [],
        pagination: INITIAL_PAGINATION_INFO,
        resultsDescription: {
            employeeIds: [],
            sourceSystems: [],
            sourceUserIds: [],
            types: [],
            sourceRanks: [],
            importedOnDates: [],
            importedBy: [],
        },
    },
    myImportedLearningsRequestData: {
        textSearch: '',
        filters: null,
        pageNumber: 1,
        pageSize: 10,
        sortBy: IMPORTED_LEARNINGS_REQUEST_KEY.COMPLETED_DATE,
        sortOrder: 'descending',
    },
    currentGetRequestId: null,

    isGetImportedLearningResultsPending: false,
    isGetImportedLearningsFiltersValuesPending: false,
};

export const getImportedLearningResults = createAsyncThunk<
    PaginatedResponse<UserImportedLearning, ImportedLearningsFiltersValues>,
    ImportedLearningsRequest,
    { rejectValue: ImportedLearningsError }
>('importedLearnings/getImportedLearningResults', (payload, { rejectWithValue }) =>
    getMyImportedLearningResults(payload)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

const importedLearningsSlice = createSlice({
    name: 'importedLearnings',
    initialState,
    reducers: {
        updateImportedLearningsRequestData(state, action: PayloadAction<ImportedLearningsRequest>) {
            state.myImportedLearningsRequestData = action.payload;
        },
        clearImportedLearningsState() {
            return initialState;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(getImportedLearningResults.pending, (state, action) => {
            state.isGetImportedLearningResultsPending = true;
            state.error = null;
            state.currentGetRequestId = action.meta.requestId;
        });
        builder.addCase(getImportedLearningResults.fulfilled, (state, action) => {
            // Search fires on every key press; ignore responses of older requests.
            if (state.currentGetRequestId !== action.meta.requestId) return;

            state.isGetImportedLearningResultsPending = false;
            state.currentGetRequestId = null;
            state.myImportedLearningResults = action.payload;
        });
        builder.addCase(getImportedLearningResults.rejected, (state, action) => {
            if (state.currentGetRequestId !== action.meta.requestId) return;

            state.isGetImportedLearningResultsPending = false;
            state.currentGetRequestId = null;
            state.error = extractError(action);
        });
    },
});

export const { updateImportedLearningsRequestData, clearImportedLearningsState } = importedLearningsSlice.actions;

export default importedLearningsSlice.reducer;
